'use client';

import { useEffect } from 'react';

/**
 * Who owns the Escape key while a modal sits over a settings panel.
 *
 * The drill-down panels (`NestedSheet`) close themselves on Escape. A modal
 * opened from INSIDE a panel — the sketch editor, the crop step of a reference
 * upload, a confirm dialog — is a layer above it, so Escape belongs to the
 * modal alone. Nothing in the DOM says so reliably: the panel keeps the focus
 * that opened the modal, and our modals are not one kind of node (some are
 * Radix dialogs, some are plain divs at `z-100000`). Sniffing z-index or
 * `role="dialog"` guesses; a modal that says "I am open" does not.
 *
 * So every modal surface holds this while it is mounted, and a panel checks it
 * before answering Escape. Reference-counted — modals can stack.
 *
 * Escape only. What paints above what stays a matter of z-index: the panel
 * stack lives under 50, the modal layer at 50 and up (see NestedSheet).
 */
let openModals = 0;

/** True while any modal surface is mounted over the page. */
export function isModalLayerOpen() {
  return openModals > 0;
}

/** Claim the modal layer for as long as the calling component is mounted. */
export function useModalLayer(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    openModals += 1;
    return () => {
      openModals -= 1;
    };
  }, [enabled]);
}
